import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "utsava-theme-v2";

/** The seven shipped visual identities. `swatch`/`swatch2` drive the picker dots,
 * every other colour in the app resolves through the CSS variables keyed off
 * `[data-theme='<id>']` in index.css. */
export const THEMES = [
    { id: "sentinel", label: "Sentinel Crimson", short: "Sentinel", blurb: "Blood red on machined steel", swatch: "#FF3B4E", swatch2: "#F5A623" },
    { id: "gold", label: "Genesis Gold", short: "Gold", blurb: "Brushed brass on black", swatch: "#D4AF37", swatch2: "#FDE047" },
    { id: "cyber", label: "Cyber Blue", short: "Cyber", blurb: "Electric neon HUD", swatch: "#00F0FF", swatch2: "#0044FF" },
    { id: "galactic", label: "Galactic Orbit", short: "Galactic", blurb: "Violet nebula starfield", swatch: "#C4B5FD", swatch2: "#7C3AED" },
    { id: "liquid", label: "Liquid Glass", short: "Liquid", blurb: "Translucent frosted glass", swatch: "#7DD3FC", swatch2: "#818CF8" },
    { id: "brutal", label: "Neo Brutalist Terminal", short: "Terminal", blurb: "Flat black, phosphor green", swatch: "#4ADE80", swatch2: "#0A0A0A" },
    { id: "command", label: "Cinematic Command Center", short: "Command", blurb: "Sunset amber over steel", swatch: "#FB923C", swatch2: "#60A5FA" },
];

const DEFAULT_THEME = "sentinel";
const isValid = (id) => THEMES.some((t) => t.id === id);

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {}, toggleTheme: () => {} });

/** Applies the theme as a data-attribute on <html>. Every colour in the app
 * resolves through CSS variables keyed off that attribute (see index.css and
 * the themed Tailwind scales), so switching re-skins the whole workspace. */
export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return isValid(stored) ? stored : DEFAULT_THEME;
    });

    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        localStorage.setItem(STORAGE_KEY, theme);
        window.dispatchEvent(new CustomEvent("utsava-theme-change", { detail: theme }));
    }, [theme]);

    const setTheme = useCallback((next) => {
        if (isValid(next)) setThemeState(next);
    }, []);
    const toggleTheme = useCallback(() => {
        setThemeState((t) => {
            const i = THEMES.findIndex((x) => x.id === t);
            return THEMES[(i + 1) % THEMES.length].id;
        });
    }, []);
    const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);

/** Reads a resolved CSS variable — used where a library needs real colour
 * values instead of classes (Mermaid, canvas exports). */
export function themeVar(name, fallback = "") {
    if (typeof window === "undefined") return fallback;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}
